import './styles/styles.css';
import './styles/export.css';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import { I18nProvider } from './i18n.ts';
import ErrorBoundary from './components/shared/ErrorBoundary.tsx';
import { ToastProvider } from './components/shared/Toast.tsx';
import { ModalProvider } from './components/shared/ModalDialogs.tsx';
import { checkAndBumpCacheVersion } from './services/CacheVersion.ts';
import {
  checkBuildVersion,
  installPeriodicBuildVersionCheck,
} from './services/BuildVersionCheck.ts';
import { installChunkReloadGuard } from './services/ChunkReloadGuard.ts';
import { installConsoleErrorReporting } from './services/errorReporter.ts';

// Invalidate stale localStorage cache (af_offline_*) on version bump.
// Must run before any code that reads from the cache.
checkAndBumpCacheVersion();

// #767: Detect mismatch tussen geladen client BUILD_SHA en server BUILD_SHA.
// Bij mismatch: cleanup stale localStorage + emit event voor BuildVersionBanner.
// Niet-blokkerend — async check loopt terwijl app start.
checkBuildVersion();

// #1065: Graceful recovery bij verouderde lazy-chunks na een deploy.
// - installChunkReloadGuard: vangt `vite:preloadError` op en herlaadt éénmalig
//   stil (met cooldown-guard) i.p.v. het crash-scherm te tonen.
// - installPeriodicBuildVersionCheck: her-checkt de build-versie bij tab-refocus
//   zodat een lange sessie proactief ververst vóór een navigatie-crash.
installChunkReloadGuard();
// #1652 — afgevangen fouten (console.error) ook naar error_logs; alleen in productie.
if (import.meta.env.PROD) installConsoleErrorReporting();
installPeriodicBuildVersionCheck();

/**
 * Load application configuration from backend before starting the app.
 */
const loadAppConfig = async () => {
  try {
    const response = await fetch('/api/config');
    const config = await response.json();
    return config;
  } catch (error) {
    console.warn('⚠️ Failed to load app config:', error);
    return { hasDatabase: false };
  }
};

/**
 * Load Google Maps API key from authenticated endpoint and inject the script.
 * Called after user login — key is not exposed to unauthenticated visitors.
 */
let _googleMapsLoading = false;
(window as any).__loadGoogleMaps = async () => {
  if (_googleMapsLoading || document.getElementById('google-maps-sdk')) return;
  _googleMapsLoading = true;
  if (typeof window !== 'undefined') {
    try {
      const response = await fetch('/api/maps/config', { credentials: 'include' });
      if (!response.ok) return;
      const { googleMapsApiKey } = await response.json();
      if (!googleMapsApiKey) {
        console.warn('⚠️  GOOGLE_MAPS_API_KEY not configured. Maps functionality will be limited.');
        return;
      }
      const script = document.createElement('script');
      script.id = 'google-maps-sdk';
      // Bewust ZONDER `loading=async`.
      //
      // Met die vlag vult de SDK `google.maps` alleen met `importLibrary()`, en
      // moet elke consument `await google.maps.importLibrary('maps')` doen. Onze
      // schermen gebruiken het klassieke `new google.maps.Map(...)`. Resultaat:
      // `TypeError: google.maps.Map is not a constructor` — PlaceManager viel
      // daarmee in de ErrorBoundary en toonde "Oeps! Er is iets misgegaan" in
      // plaats van een kaart.
      //
      // `places` staat erbij omdat het dashboard-widget dat nodig heeft; dat had
      // een eigen scriptregel met andere parameters, en twee verschillende
      // laadwijzen van dezelfde SDK naast elkaar is precies hoe dit misging.
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&v=weekly&libraries=marker,places`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    } catch (error) {
      _googleMapsLoading = false;
      console.warn('⚠️ Failed to load Google Maps config:', error);
    }
  }
};

// Initialize app after loading configuration
(async () => {
  await loadAppConfig();

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Could not find root element to mount to');
  }

  const root = createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <I18nProvider>
            <ToastProvider>
              <ModalProvider>
                <App />
              </ModalProvider>
            </ToastProvider>
          </I18nProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );
})();
